<script setup>
import { useActiveSection } from 'composables/active-section';
import { usePageMeta } from 'composables/page-meta';
import { buildPageMeta } from 'src/utils/page-meta-util';

const notes = [
    { id: 'note-1', title: 'Coverage and active listings' },
    { id: 'note-2', title: 'Advertised salary evidence' },
    { id: 'note-3', title: '30-day movement' },
    { id: 'note-4', title: 'Advertising organisations' },
    { id: 'note-5', title: 'Breakdowns and refresh' },
    { id: 'note-6', title: 'Xprt Dev and Xprt Jobs' },
    { id: 'note-7', title: 'Corrections' }
];

const { activeId, followAnchor } = useActiveSection(notes.map(note => note.id));

usePageMeta(() => buildPageMeta({
    title: 'Australian Tech Market Methodology',
    description: 'How Xprt Dev measures Australian ICT job demand, advertised salaries and hiring trends. Understand '
        + 'data sources, sample limits and refresh timing.',
    canonicalUrl: 'https://xprt.dev/methodology',
    type: 'article'
}));

function noteLinkClasses(note) {
    return [
        'contents-link row items-baseline no-wrap q-py-xs q-pl-sm text-body2',
        { 'contents-link--active': note.id === activeId.value }
    ];
}
</script>

<template>
    <main class="methodology-page">
        <header class="market-hero">
            <div class="container-md q-px-md q-py-xl">
                <div class="hero-copy q-py-lg">
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-md">The notes · How every figure is counted</p>
                    <h1 class="text-h3 text-sm-h2 text-md-h1 text-weight-bold q-mt-none q-mb-md">Notes on the figures.</h1>
                    <p class="hero-deck text-h6 text-weight-regular text-hint q-my-none">
                        What the market pages measure, where each figure stops, and when a value is withheld rather than
                        shown as zero. Every numbered marker on a market page points to one of these notes.
                    </p>
                </div>
            </div>
        </header>
        <div class="contents-layout container-md q-px-md q-py-xl">
            <aside aria-labelledby="contents-heading" class="contents-rail">
                <p id="contents-heading" class="font-mono text-caption text-uppercase text-hint q-mt-none q-mb-sm">On this page</p>
                <nav aria-label="Notes">
                    <ol class="contents-list q-pl-none q-my-none">
                        <li v-for="(note, index) in notes" :key="note.id">
                            <a
                                :aria-current="note.id === activeId ? 'location' : undefined"
                                :class="noteLinkClasses(note)"
                                :href="`#${note.id}`"
                                @click="followAnchor(note.id)"
                            >
                                <span class="font-mono text-caption text-hint q-mr-sm">{{ index + 1 }}</span>
                                <span class="note-title">{{ note.title }}</span>
                            </a>
                        </li>
                    </ol>
                </nav>
            </aside>
            <article class="contents-copy">
                <section id="note-1" class="note">
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-xs">Note 1</p>
                    <h2 class="text-h4 text-weight-bold q-mt-none">Coverage and active listings</h2>
                    <p>
                        The market pages count distinct, currently active Australian roles classified under Information &amp;
                        Communication Technology or one of its direct specialisations. The calculation reads Xprt Jobs'
                        active-listing statistics view; expired and inactive listings are outside the snapshot.
                    </p>
                    <p>
                        Coverage depends on the sources currently collected, their publication patterns and how well each
                        listing can be classified. It is not a census of every vacancy or worker in Australia, and a change
                        in source coverage can move a figure even when employer behaviour has not changed.
                    </p>
                </section>
                <section id="note-2" class="note">
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-xs">Note 2</p>
                    <h2 class="text-h4 text-weight-bold q-mt-none">Advertised salary evidence</h2>
                    <p>
                        Salary measures use listings with a usable advertised remuneration range. Annual, monthly, daily and
                        hourly amounts are normalised to an annual Australian-dollar basis using fixed cycle multipliers. The
                        median and the 25th to 75th percentile range describe that observed advertised-pay sample; they are
                        not an estimate of total compensation or of what everyone working in the field earns. Salary figures
                        are shown to the nearest $100, because the percentiles fall between advertised salaries.
                    </p>
                    <p>
                        The salary strip draws the same sample as bands of advertised pay. The bands cover unequal ranges, so
                        each bar's height shows its share of listings per $10k of pay it spans, and a wide band is not drawn
                        taller for covering more of the range. The yellow marker is the median and the dashed lines are the
                        lower and upper quartiles.
                    </p>
                    <q-banner class="notice-banner q-my-lg">
                        <strong>Minimum sample: three active listings with usable advertised pay.</strong>
                        <div class="text-body2 q-mt-xs">
                            Below that threshold, salary values and bands are withheld. The page reports the observed sample
                            count instead of displaying $0.
                        </div>
                    </q-banner>
                    <p>
                        Xprt Dev collects only advertisements that state their pay. A listing whose advertised salary cannot
                        be read is recorded so it is not fetched again, and is never published into the inventory these
                        pages measure. Every figure here therefore describes the part of the market that advertises pay,
                        which is a subset of all advertised roles, and a share of listings carrying pay would always be one
                        hundred per cent.
                    </p>
                    <p>
                        Salary measures count each listing once, even when it is advertised in more than one place, so a
                        listing advertised widely carries no more weight than one advertised once.
                    </p>
                </section>
                <section id="note-3" class="note">
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-xs">Note 3</p>
                    <h2 class="text-h4 text-weight-bold q-mt-none">30-day movement</h2>
                    <p>
                        The ICT overview fits a linear trend across the daily active-listing snapshots held for the previous
                        30 days and requires at least seven of them. The fitted slope is scaled to a month, so the figure is a
                        rate of movement rather than the difference between the count today and a count taken 30 days ago, and
                        the window holds fewer than 30 days while snapshots are still accumulating. A calculated change above
                        2% is labelled up, below -2% is labelled down, and the range between them is labelled stable.
                    </p>
                    <p>
                        This describes movement in observed active listings. It does not predict future demand. Specialisation
                        pages leave the trend out because the current series rolls up to broad ICT and must not be presented as
                        if it were specific to a child classification.
                    </p>
                </section>
                <section id="note-4" class="note">
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-xs">Note 4</p>
                    <h2 class="text-h4 text-weight-bold q-mt-none">Advertising organisations</h2>
                    <p>
                        Organisation counts are distinct active listings associated with each advertiser in the selected
                        market. They are ranked by active listing count, and the page shows only the leading organisations.
                        An advertiser is either the employer or a recruitment agency, and an agency may be recruiting for
                        employers it does not name, so its count can gather several employers' roles. A count indicates
                        visible hiring activity in Xprt's inventory, not headcount, hires made or employer size.
                    </p>
                </section>
                <section id="note-5" class="note">
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-xs">Note 5</p>
                    <h2 class="text-h4 text-weight-bold q-mt-none">Breakdowns and refresh</h2>
                    <p>
                        Work-arrangement and employment-type breakdowns include only listings with a usable value for that
                        field, so their totals can differ from the headline active-role count. A listing advertised in more
                        than one place counts once for each place in the work-arrangement breakdown. While a market holds
                        fewer listings than the minimum sample, its breakdowns and organisations are withheld, as its salary
                        figures are, rather than shown as shares of one or two listings. Listings may be edited, removed,
                        duplicated at source or classified imperfectly.
                    </p>
                    <p>
                        The active-job statistics view is scheduled to refresh every 15 minutes, and daily snapshots feed the
                        30-day trend. A delayed source, a failed refresh or a catalogue change can make the displayed data older
                        or temporarily unavailable, so the schedule is not a guarantee that every figure is exactly 15 minutes
                        old.
                    </p>
                </section>
                <section id="note-6" class="note">
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-xs">Note 6</p>
                    <h2 class="text-h4 text-weight-bold q-mt-none">Xprt Dev and Xprt Jobs</h2>
                    <p>
                        Xprt Dev owns the market view and these notes. Xprt Jobs owns job search, job detail, applications and
                        employer services. "View jobs on Xprt Jobs" opens the stable Australian category page; it does not
                        promise a shared login or carry an application across.
                    </p>
                </section>
                <section id="note-7" class="note">
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-xs">Note 7</p>
                    <h2 class="text-h4 text-weight-bold q-mt-none">Corrections</h2>
                    <p>
                        If a figure looks wrong, email
                        <a href="mailto:team@xprt.dev?subject=Market%20data%20correction">team@xprt.dev</a> with the page
                        URL, the figure, when you saw it and why it concerns you. We review the source and the calculation,
                        correct confirmed errors, and never quietly widen a sample to make a metric appear available.
                    </p>
                </section>
                <q-btn color="primary" href="/market" icon="sym_r_arrow_back" label="Return to the ICT market" no-caps outline/>
            </article>
        </div>
    </main>
</template>

<style lang="scss" scoped>
.methodology-page {
    .hero-copy {
        max-width: 880px;
    }

    .hero-deck {
        max-width: 720px;
    }

    .note + .note {
        border-top: 1px solid rgba($grey-7, 0.2);
        margin-top: 40px;
        padding-top: 40px;
    }

    .note:last-of-type {
        margin-bottom: 40px;
    }
}
</style>
